/**
 * Play United — Motion System
 * ----------------------------------------------------------------------
 * Lightweight, dependency-free scroll-reveal + parallax controller.
 * No animation library — IntersectionObserver + rAF-throttled scroll only.
 *
 * Usage (declarative, add attributes in markup — no JS calls needed):
 *
 *   data-reveal                     Fade + rise in when scrolled into view.
 *   data-reveal-trigger="load"      Reveal shortly after page load instead
 *                                    of on scroll (use for above-the-fold
 *                                    content like the hero headline).
 *   data-reveal-type="mask"         Use a clip-path wipe instead of the
 *                                    default fade/rise (headlines).
 *   data-reveal-delay="120"         Stagger delay in ms (e.g. grid cards:
 *                                    delay = index * 80, capped).
 *   data-parallax="0.15"            Scroll-linked parallax factor. Put on
 *                                    the moving element (usually an <img>),
 *                                    not its positioned wrapper, so it
 *                                    doesn't fight an existing transform
 *                                    (e.g. a translateY(-50%) centering
 *                                    transform on the wrapper).
 *   data-parallax-root              Put on the nearest ancestor section —
 *                                    used only as a cheap "is this near the
 *                                    viewport" visibility gate for the
 *                                    parallax elements inside it.
 *
 * Added: 2026-07-09 — awwwards animation/layout brainstorm, pass 1.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Reveal (scroll-triggered + load-triggered) ---------------- */
  function initReveal() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    els.forEach(function (el) {
      var delay = el.getAttribute('data-reveal-delay');
      if (delay) el.style.setProperty('--pu-reveal-delay', delay + 'ms');
    });

    if (prefersReducedMotion) {
      els.forEach(function (el) { el.classList.add('pu-revealed'); });
      return;
    }

    var loadEls = [];
    var scrollEls = [];
    els.forEach(function (el) {
      if (el.getAttribute('data-reveal-trigger') === 'load') {
        loadEls.push(el);
      } else {
        scrollEls.push(el);
      }
    });

    // Above-the-fold elements: reveal shortly after paint, not on scroll —
    // otherwise they'd need to already be "scrolled into view" (they are,
    // by definition, on load) which IntersectionObserver won't fire for
    // reliably across browsers on initial paint.
    if (loadEls.length) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          loadEls.forEach(function (el) { el.classList.add('pu-revealed'); });
        });
      });
    }

    if (!scrollEls.length) return;

    if (!('IntersectionObserver' in window)) {
      scrollEls.forEach(function (el) { el.classList.add('pu-revealed'); });
      return;
    }

    // GOTCHA (fixed 2026-07-09): mask-type elements start FULLY clipped
    // (clip-path inset 100%), and a fully-clipped element has zero visible
    // area — IntersectionObserver NEVER fires for it, so the reveal that
    // un-clips it never runs. For masks we therefore observe a non-clipped
    // proxy (the parent element) and reveal the mask when the proxy enters.
    var revealTargets = new WeakMap(); // watched element -> [reveal targets]
    function watch(watchEl, revealEl) {
      var list = revealTargets.get(watchEl);
      if (list) { list.push(revealEl); }
      else { revealTargets.set(watchEl, [revealEl]); }
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var targets = revealTargets.get(entry.target) || [entry.target];
          targets.forEach(function (t) { t.classList.add('pu-revealed'); });
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

    scrollEls.forEach(function (el) {
      if (el.getAttribute('data-reveal-type') === 'mask' && el.parentElement) {
        watch(el.parentElement, el);
        observer.observe(el.parentElement);
      } else {
        watch(el, el);
        observer.observe(el);
      }
    });
  }

  /* ---------------- Parallax (scroll-linked, GPU transform only) ---------------- */
  function initParallax() {
    if (prefersReducedMotion) return;
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
    if (!els.length) return;

    var layers = els.map(function (el) {
      return {
        el: el,
        speed: parseFloat(el.getAttribute('data-parallax')) || 0.2,
        root: el.closest('[data-parallax-root]') || el.parentElement
      };
    });

    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      var scrollY = window.scrollY || window.pageYOffset;
      layers.forEach(function (layer) {
        var rect = layer.root.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return; // off-screen, skip work
        layer.el.style.transform = 'translate3d(0,' + (scrollY * layer.speed).toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  function init() {
    initReveal();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
